import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import AdminPageShell from '../AdminPageShell';
import AdminToast from '../AdminToast';
import AdminTableDeleteButton from '../AdminTableDeleteButton';
import AcademiaSelect from '../../common/AcademiaSelect';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import {
  createAdminAmbassadeur,
  deleteAdminAmbassadeur,
  fetchAdminAmbassadeurs,
  patchAdminAmbassadeur,
  resendAmbassadeurAccess,
  uploadAdminRegistrationDocument,
} from '../../../services/academiaAdminService';
import { formatMoney } from '../../../utils/currency';
import { isAdministrateur } from '../../../utils/adminAccess';

const INPUT_CLASS =
  'w-full rounded-lg border border-outline-variant/30 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15';

function isImageFile(file: File) {
  return file.type.startsWith('image/');
}

function isPdfFile(file: File) {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

const BTN_TABLE =
  'academic-panel-btn academic-panel-btn--secondary !inline-flex !px-2.5 !py-1.5 text-xs !gap-1.5 whitespace-nowrap';
const BTN_TABLE_PRIMARY =
  'academic-panel-btn academic-panel-btn--primary !inline-flex !px-2.5 !py-1.5 text-xs !gap-1.5 whitespace-nowrap';
const BTN_TABLE_DANGER =
  'academic-panel-btn academic-panel-btn--danger !inline-flex !px-2.5 !py-1.5 text-xs !gap-1.5 whitespace-nowrap';

const ZONE_PROMOTION_OPTIONS = [
  'Réseaux sociaux',
  'Campus / universités',
  'Écoles / instituts',
  'Quartier / communauté',
  'Entreprises / collègues',
  'Églises / associations',
  'Événements / terrain',
];

type Row = {
  id: string;
  nomComplet: string;
  telephone: string;
  email?: string;
  codeParrainage: string;
  zonePromotion?: string | null;
  nombreFilleuls: number;
  soldeWallet: number;
  profilComplet: boolean;
  estActif: boolean;
  doitChangerMotDePasse: boolean;
};

export default function AcademiaAmbassadeursPanel({ user }: { user?: { role?: string } | null }) {
  const canDelete = isAdministrateur(user);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    prenom: '',
    nom: '',
    telephone: '',
    email: '',
    zonePromotion: '',
  });
  const [pieceIdentiteFile, setPieceIdentiteFile] = useState<File | null>(null);
  const [piecePreviewUrl, setPiecePreviewUrl] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRows(await fetchAdminAmbassadeurs());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!pieceIdentiteFile) {
      setPiecePreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(pieceIdentiteFile);
    setPiecePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pieceIdentiteFile]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);
    try {
      let pieceIdentiteUrl: string | undefined;
      let uploadToken: string | undefined;
      if (pieceIdentiteFile) {
        const uploaded = await uploadAdminRegistrationDocument(pieceIdentiteFile);
        pieceIdentiteUrl = uploaded.path;
        uploadToken = uploaded.uploadToken ?? undefined;
      }
      const result = await createAdminAmbassadeur({
        ...createForm,
        ...(pieceIdentiteUrl ? { pieceIdentiteUrl, uploadToken } : {}),
      });
      setToast({ message: result.message ?? 'Ambassadeur créé.', variant: 'success' });
      setShowCreate(false);
      setCreateForm({ prenom: '', nom: '', telephone: '', email: '', zonePromotion: '' });
      setPieceIdentiteFile(null);
      await load();
    } catch (err) {
      setToast({ message: err instanceof Error ? err.message : 'Création impossible', variant: 'error' });
    } finally {
      setCreating(false);
    }
  }

  return (
  <>
    <AdminPageShell
      title="Ambassadeurs"
      description="Comptes chargés de la promotion de la plateforme via code de parrainage."
      isLoading={loading}
    >
      <AdminToast message={toast?.message ?? null} variant={toast?.variant ?? 'success'} onClose={() => setToast(null)} />
      {error ? <p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p> : null}

      <div className="mb-4 flex justify-end">
        <button
          type="button"
          className="rounded-lg border border-primary px-3 py-1.5 text-sm font-bold text-primary"
          onClick={() => {
            setShowCreate((v) => !v);
            setPieceIdentiteFile(null);
          }}
        >
          + Nouvel ambassadeur
        </button>
      </div>

      {showCreate ? (
        <form className="mb-6 space-y-3 rounded-xl border p-4" onSubmit={handleCreate}>
          <p className="text-sm text-on-surface-variant">
            Un mot de passe provisoire à 6 chiffres est envoyé par e-mail. L&apos;ambassadeur complète son profil puis
            choisit son mot de passe.
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(
              [
                ['prenom', 'Prénom'],
                ['nom', 'Nom'],
                ['telephone', 'Téléphone'],
                ['email', 'E-mail *'],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="block text-sm">
                <span className="text-xs font-bold text-on-surface-variant">{label}</span>
                <input
                  className={`${INPUT_CLASS} mt-1`}
                  value={createForm[key]}
                  onChange={(e) => setCreateForm({ ...createForm, [key]: e.target.value })}
                  required={key === 'email' || key === 'prenom' || key === 'nom'}
                />
              </label>
            ))}
            <label className="block text-sm">
              <span className="text-xs font-bold text-on-surface-variant">Zone de promotion</span>
              <AcademiaSelect
                className="mt-1"
                value={createForm.zonePromotion}
                onChange={(e) => setCreateForm({ ...createForm, zonePromotion: e.target.value })}
              >
                <option value="">Sélectionner une zone</option>
                {ZONE_PROMOTION_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </AcademiaSelect>
            </label>
            <div className="space-y-3 sm:col-span-2 lg:col-span-3">
              <label className="block text-sm">
                <span className="text-xs font-bold text-on-surface-variant">
                  Pièce d&apos;identité{' '}
                  <span className="font-medium normal-case">(optionnel — image ou PDF)</span>
                </span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,.pdf"
                  className={`${INPUT_CLASS} mt-1`}
                  onChange={(e) => setPieceIdentiteFile(e.target.files?.[0] ?? null)}
                />
                <span className="mt-1 block text-[11px] text-on-surface-variant">
                  {pieceIdentiteFile
                    ? `Fichier sélectionné : ${pieceIdentiteFile.name}`
                    : 'Non obligatoire. Formats acceptés : JPG, PNG, WebP, GIF, PDF.'}
                </span>
              </label>

              <div className="rounded-xl border border-dashed border-outline-variant/35 bg-slate-50/80 p-3">
                <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-on-surface-variant">
                  Aperçu de la pièce
                </p>
                {!pieceIdentiteFile || !piecePreviewUrl ? (
                  <p className="py-8 text-center text-sm text-on-surface-variant">
                    Aucun fichier sélectionné — l&apos;aperçu apparaîtra ici.
                  </p>
                ) : isImageFile(pieceIdentiteFile) ? (
                  <img
                    src={piecePreviewUrl}
                    alt="Aperçu pièce d'identité"
                    className="mx-auto max-h-64 max-w-full rounded-lg border border-outline-variant/20 bg-white object-contain shadow-sm"
                  />
                ) : isPdfFile(pieceIdentiteFile) ? (
                  <iframe
                    title="Aperçu PDF pièce d'identité"
                    src={piecePreviewUrl}
                    className="h-72 w-full rounded-lg border border-outline-variant/20 bg-white shadow-sm"
                  />
                ) : (
                  <p className="py-6 text-center text-sm text-on-surface-variant">
                    Aperçu non disponible pour ce type de fichier ({pieceIdentiteFile.name}).
                  </p>
                )}
                {pieceIdentiteFile ? (
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      className="text-xs font-semibold text-red-700 hover:underline"
                      onClick={() => setPieceIdentiteFile(null)}
                    >
                      Retirer le fichier
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <button
            type="submit"
            disabled={creating}
            className="academic-panel-btn academic-panel-btn--primary disabled:opacity-60"
          >
            {creating ? 'Création…' : "Créer et envoyer l'invitation"}
          </button>
        </form>
      ) : null}

      <div className="overflow-x-auto rounded-xl border">
        <table className="min-w-full text-sm">
          <thead className="bg-surface-container-low text-left text-xs font-bold uppercase text-on-surface-variant">
            <tr>
              <th className="px-4 py-3">Ambassadeur</th>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Filleuls</th>
              <th className="px-4 py-3">Wallet</th>
              <th className="px-4 py-3">Statut</th>
              <th className="whitespace-nowrap px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t">
                <td className="px-4 py-3">
                  <span className="block font-semibold text-slate-900">{r.nomComplet}</span>
                  <span className="text-xs text-on-surface-variant">{r.email ?? r.telephone}</span>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{r.codeParrainage}</td>
                <td className="px-4 py-3">{r.nombreFilleuls}</td>
                <td className="px-4 py-3">{formatMoney(r.soldeWallet, 'CDF')}</td>
                <td className="px-4 py-3">
                  {!r.estActif ? 'Inactif' : r.profilComplet ? 'Actif' : 'Profil incomplet'}
                  {r.doitChangerMotDePasse ? ' · MDP provisoire' : ''}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right">
                  <div className="inline-flex flex-nowrap items-center justify-end gap-2">
                    <Link
                      to={`/admin/ambassadeurs/${encodeURIComponent(r.id)}`}
                      state={{ ambassadeurLabel: r.nomComplet }}
                      className={BTN_TABLE_PRIMARY}
                    >
                      <LucideSymbolIcon name="visibility" tone="inherit" size={14} />
                      Détail
                    </Link>
                    <button
                      type="button"
                      className={BTN_TABLE}
                      onClick={async () => {
                        try {
                          const res = await resendAmbassadeurAccess(r.id);
                          setToast({ message: res.message ?? 'Accès renvoyé.', variant: 'success' });
                        } catch (err) {
                          setToast({
                            message: err instanceof Error ? err.message : 'Échec',
                            variant: 'error',
                          });
                        }
                      }}
                    >
                      <LucideSymbolIcon name="forward_to_inbox" tone="inherit" size={14} />
                      Renvoyer accès
                    </button>
                    <button
                      type="button"
                      className={r.estActif ? BTN_TABLE_DANGER : BTN_TABLE_PRIMARY}
                      onClick={async () => {
                        try {
                          await patchAdminAmbassadeur(r.id, { estActif: !r.estActif });
                          await load();
                        } catch (err) {
                          setToast({
                            message: err instanceof Error ? err.message : 'Échec',
                            variant: 'error',
                          });
                        }
                      }}
                    >
                      <LucideSymbolIcon
                        name={r.estActif ? 'block' : 'check_circle'}
                        tone="inherit"
                        size={14}
                      />
                      {r.estActif ? 'Désactiver' : 'Activer'}
                    </button>
                    <AdminTableDeleteButton
                      canDelete={canDelete}
                      disabled={deletingId === r.id}
                      confirmMessage={`Supprimer définitivement l'ambassadeur ${r.nomComplet} ? Cette action est irréversible.`}
                      onDelete={async () => {
                        setDeletingId(r.id);
                        try {
                          await deleteAdminAmbassadeur(r.id);
                          await load();
                          setToast({ message: 'Ambassadeur supprimé.', variant: 'success' });
                        } catch (err) {
                          setToast({
                            message: err instanceof Error ? err.message : 'Suppression impossible.',
                            variant: 'error',
                          });
                        } finally {
                          setDeletingId(null);
                        }
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && !loading ? (
          <p className="p-6 text-sm text-on-surface-variant">Aucun ambassadeur enregistré.</p>
        ) : null}
      </div>
    </AdminPageShell>
  </>
  );
}
